import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Tag, Plus, Pencil, Trash2 } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TiposActividad() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nombre, setNombre] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/tipos-actividad`, { headers }); setTipos(res.data); } catch { }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setNombre(''); setShowModal(true); };
  const openEdit = (t) => { setEditing(t); setNombre(t.nombre); setShowModal(true); };

  const handleSave = async () => {
    if (!nombre.trim()) { toast.error('Ingrese un nombre'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/tipos-actividad/${editing.id}`, { nombre }, { headers });
        toast.success('Tipo actualizado');
      } else {
        await axios.post(`${API}/tipos-actividad`, { nombre }, { headers });
        toast.success('Tipo creado');
      }
      setShowModal(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = (id) => {
    setConfirmDelete({
      onConfirm: async () => {
        try { await axios.delete(`${API}/tipos-actividad/${id}`, { headers }); toast.success('Eliminado'); fetchData(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmDelete(null);
      }
    });
  };

  return (
    <div className="space-y-6" data-testid="tipos-actividad-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tipos de Actividad</h1>
          <p className="text-sm text-slate-500 mt-0.5">Administre los tipos de actividad operacional</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] transition-colors" data-testid="nuevo-tipo-btn">
          <Plus className="w-4 h-4" />Nuevo Tipo
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B919B]" /></div>
        ) : tipos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16"><Tag className="w-12 h-12 text-slate-300 mb-3" /><p className="text-sm text-slate-500">No hay tipos de actividad registrados</p></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {tipos.map(t => (
              <div key={t.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50/50" data-testid={`tipo-${t.id}`}>
                <div className="flex items-center gap-3">
                  <Tag className="w-4 h-4 text-[#a05e49]" />
                  <span className="text-sm text-slate-700 font-medium">{t.nombre}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(t)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDelete(t.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-slate-900 mb-4">{editing ? 'Editar Tipo' : 'Nuevo Tipo'}</h2>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Nombre *</label>
              <input type="text" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Operacion Mina"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20" data-testid="modal-input-nombre"
                onKeyDown={e => e.key === 'Enter' && handleSave()} />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm bg-[#a05e49] text-white rounded-lg hover:bg-[#8b4f3d]" data-testid="modal-save-btn">{editing ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      , document.body)}
      <ConfirmModal open={!!confirmDelete} title="Eliminar tipo" message="Esta seguro de eliminar este tipo de actividad?"
        confirmLabel="Eliminar" variant="danger" onConfirm={confirmDelete?.onConfirm} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
}
